import React, { useEffect, useMemo, useState } from 'react';
import dagre from 'dagre';
import {
  Position,
  ReactFlowProvider,
  type Edge,
  type Node,
} from '@xyflow/react';
import { ExternalLink } from 'lucide-react';
import { ResourceType } from '@cotera/api';
import { Text } from '@cotera/client/app/components/ui';
import { Canvas } from '@cotera/client/v0/components/ai-elements/canvas';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cotera/client/v0/components/ui/select';
import { useQuery } from '@tanstack/react-query';
import { useTenantedClient, useWhoami } from '@cotera/client/app/stores/org';
import { queryKeys } from '@cotera/client/app/hooks/query-cache-keys';
import { useArtifactPanel } from '@cotera/client/v0/chat/artifact-panel-context';
import { WORKFLOW_GRAPH_VIEW_TYPE } from '../../../workflow-artifact-views';
import { useWorkflowOptions } from '@cotera/client/app/v1/trigger/hooks';
import { useToolCallUpstreamSamples } from '../../../hooks/use-tool-call-upstream-samples';
import { useWorkflowModel } from '../../../workflow.model';
import {
  WORKFLOW_EDGE_OPTIONS,
  WORKFLOW_EDGE_TYPE,
} from '../../../workflow-edge-style';
import {
  WORKFLOW_FLOW_NODE_TYPE,
  WORKFLOW_FLOW_PREVIEW_NODE_TYPES,
  WORKFLOW_FLOW_TRIGGER_TYPE,
} from '../../index';
import { workflowTriggerViewFromSnapshot } from '../../trigger';
import {
  resolveWorkflowEntryRunInputSpec,
  type WorkflowEntryRunInputSpec,
} from '../../../workflow-entry-run-input';
import type { WorkflowGraphApiData } from '../../../workflow-graph.types';
import { asRecord, readString } from '../../utils';
import { buildUpstreamSources } from './variable-picker-data';
import {
  UpstreamRefChip,
  UpstreamRefPicker,
  UpstreamRefPickerButton,
} from './upstream-ref-picker';
import type { WorkflowNodeEditorProps } from './types';

const RUN_WORKFLOW_PREVIEW_NODE_WIDTH = 240;
const RUN_WORKFLOW_PREVIEW_NODE_HEIGHT = 120;

function layoutRunWorkflowPreviewFlow(params: {
  nodes: Node[];
  edges: Edge[];
}): { nodes: Node[]; edges: Edge[] } {
  const dagreGraph = new dagre.graphlib.Graph();
  dagreGraph.setDefaultEdgeLabel(() => ({}));
  dagreGraph.setGraph({
    rankdir: 'TB',
    nodesep: 72,
    ranksep: 96,
    marginx: 24,
    marginy: 24,
  });

  for (const node of params.nodes) {
    dagreGraph.setNode(node.id, {
      width: RUN_WORKFLOW_PREVIEW_NODE_WIDTH,
      height: RUN_WORKFLOW_PREVIEW_NODE_HEIGHT,
    });
  }
  for (const edge of params.edges) {
    dagreGraph.setEdge(edge.source, edge.target);
  }

  dagre.layout(dagreGraph);

  return {
    nodes: params.nodes.map((node) => {
      const point = dagreGraph.node(node.id) as { x: number; y: number };
      return {
        ...node,
        position: {
          x: point.x - RUN_WORKFLOW_PREVIEW_NODE_WIDTH / 2,
          y: point.y - RUN_WORKFLOW_PREVIEW_NODE_HEIGHT / 2,
        },
        sourcePosition: Position.Bottom,
        targetPosition: Position.Top,
      };
    }),
    edges: params.edges.map((edge) => ({ ...edge, type: WORKFLOW_EDGE_TYPE })),
  };
}

function runWorkflowPreviewGraphToFlow(graph: WorkflowGraphApiData): {
  nodes: Node[];
  edges: Edge[];
} {
  const nodes: Node[] = [
    ...graph.triggers.map((snapshot) => ({
      id: `trigger:${snapshot.trigger.id}`,
      type: WORKFLOW_FLOW_TRIGGER_TYPE,
      position: { x: 0, y: 0 },
      draggable: false,
      data: { trigger: workflowTriggerViewFromSnapshot(snapshot) },
    })),
    ...graph.nodes.map((node) => ({
      id: node.id,
      type: WORKFLOW_FLOW_NODE_TYPE,
      position: { x: 0, y: 0 },
      draggable: false,
      data: {
        node: {
          id: node.id,
          type: node.type,
          data: node.data,
          inputShape: node.inputShape,
          defaultOutput: node.defaultOutput,
        },
      },
    })),
  ];

  const edges: Edge[] = graph.edges.map((edge) => ({
    id: edge.id,
    source: edge.fromNodeId,
    target: edge.toNodeId,
    type: WORKFLOW_EDGE_TYPE,
  }));

  const targetNodeIds = new Set(graph.edges.map((edge) => edge.toNodeId));
  const entryNodeIds = graph.nodes
    .filter((node) => !targetNodeIds.has(node.id))
    .map((node) => node.id);
  const enabledBindings = graph.triggerBindings.filter(
    (binding) => binding.enabled
  );
  for (const binding of enabledBindings) {
    const triggerFlowId = `trigger:${binding.triggerId}`;
    if (!nodes.some((node) => node.id === triggerFlowId)) {
      continue;
    }
    for (const entryNodeId of entryNodeIds) {
      edges.push({
        id: `trigger-edge:${binding.id}:${entryNodeId}`,
        source: triggerFlowId,
        target: entryNodeId,
        type: WORKFLOW_EDGE_TYPE,
        reconnectable: false,
      });
    }
  }

  return layoutRunWorkflowPreviewFlow({ nodes, edges });
}

/** Sentinel key for scalar entry shapes — unwrapped by the runner. */
export const RUN_WORKFLOW_SCALAR_BINDING_KEY = '__scalar__';

function readInputBindings(
  data: Record<string, unknown> | null
): Record<string, string> {
  const raw = data?.['inputBindings'];
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) {
    return {};
  }
  const out: Record<string, string> = {};
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value === 'string' && value.trim() !== '') {
      out[key] = value;
    }
  }
  return out;
}

function InputBindingRow(props: {
  label: string;
  typeLabel?: string;
  required?: boolean;
  binding: string | undefined;
  sources: ReturnType<typeof buildUpstreamSources>;
  sourceLabels: Map<string, string>;
  onPick: (ref: string) => void;
  onClear: () => void;
}): React.ReactElement {
  const {
    label,
    typeLabel,
    required = false,
    binding,
    sources,
    sourceLabels,
    onPick,
    onClear,
  } = props;
  const hasBinding = binding !== undefined && binding.trim() !== '';

  return (
    <div className="border-input flex flex-col gap-2 rounded-md border p-3">
      <div className="flex items-center justify-between gap-2">
        <span className="flex min-w-0 items-center gap-1.5">
          <Text.Caption className="text-foreground truncate font-medium">
            {label}
          </Text.Caption>
          {required ? (
            <span className="text-muted-foreground text-[10px] uppercase tracking-wide">
              required
            </span>
          ) : null}
          {typeLabel !== undefined && typeLabel !== '' ? (
            <span className="text-muted-foreground truncate text-[10px]">
              {typeLabel}
            </span>
          ) : null}
        </span>
      </div>
      <div className="flex items-center gap-2">
        {hasBinding ? (
          <UpstreamRefChip
            refValue={binding}
            sourceLabels={sourceLabels}
            onRemove={onClear}
          />
        ) : (
          <UpstreamRefPicker sources={sources} onPick={onPick}>
            <button
              type="button"
              className="border-input bg-background hover:bg-muted/60 text-muted-foreground flex h-8 flex-1 items-center justify-start rounded-md border px-3 text-left text-sm"
            >
              {sources.length > 0
                ? 'Select a field…'
                : 'No upstream fields yet'}
            </button>
          </UpstreamRefPicker>
        )}
        {hasBinding ? (
          <UpstreamRefPickerButton
            sources={sources}
            onPick={onPick}
            title="Change field"
          />
        ) : null}
      </div>
    </div>
  );
}

function RunWorkflowGraphPreview(props: {
  graph: WorkflowGraphApiData | undefined;
  isLoading: boolean;
}): React.ReactElement {
  const { graph, isLoading } = props;
  const flow = useMemo(
    () => (graph !== undefined ? runWorkflowPreviewGraphToFlow(graph) : null),
    [graph]
  );

  if (isLoading) {
    return (
      <div className="bg-muted/20 flex h-full items-center justify-center rounded-md border text-sm text-muted-foreground">
        Loading workflow graph…
      </div>
    );
  }

  if (flow === null || flow.nodes.length === 0) {
    return (
      <div className="bg-muted/20 flex h-full items-center justify-center rounded-md border text-sm text-muted-foreground">
        Select a workflow to preview its graph.
      </div>
    );
  }

  return (
    <div className="h-full overflow-hidden rounded-md border bg-background">
      <ReactFlowProvider>
        <Canvas
          nodes={flow.nodes}
          edges={flow.edges}
          nodeTypes={WORKFLOW_FLOW_PREVIEW_NODE_TYPES}
          defaultEdgeOptions={WORKFLOW_EDGE_OPTIONS}
          className="h-full w-full"
          proOptions={{ hideAttribution: true }}
          nodesDraggable={false}
          nodesConnectable={false}
          edgesReconnectable={false}
          elementsSelectable={false}
          panOnDrag
          panOnScroll
          selectionOnDrag={false}
          fitView
        />
      </ReactFlowProvider>
    </div>
  );
}

/**
 * Config editor for a run-workflow node: pick the target workflow, map its
 * entry inputs from upstream via UpstreamRefPicker, and open the target as an
 * artifact.
 */
export const RunWorkflowNodeEditor: React.FC<WorkflowNodeEditorProps> = ({
  node,
  onChange,
}) => {
  const model = useWorkflowModel();
  const artifactPanel = useArtifactPanel();
  const orgId = useWhoami((s) => s.org.id);
  const client = useTenantedClient();
  const workflows = useWorkflowOptions();
  const candidates = useMemo(
    () => workflows.filter((workflow) => workflow.id !== model.workflowId),
    [model.workflowId, workflows]
  );

  const data = asRecord(node.data);
  const savedTargetId = readString(data, 'targetWorkflowId');
  const [targetWorkflowId, setTargetWorkflowId] = useState<string | undefined>(
    savedTargetId
  );
  const [inputBindings, setInputBindings] = useState<Record<string, string>>(
    () => readInputBindings(data)
  );

  const [seededNodeId, setSeededNodeId] = useState(node.id);
  useEffect(() => {
    if (seededNodeId === node.id) {
      return;
    }
    setSeededNodeId(node.id);
    const next = asRecord(node.data);
    setTargetWorkflowId(readString(next, 'targetWorkflowId'));
    setInputBindings(readInputBindings(next));
  }, [node.data, node.id, seededNodeId]);

  // `queryKeys.resource.get` caches the raw `resource.get` Result — its other
  // readers (the chat artifact access gate, the resource pages) unwrap it
  // themselves, so narrow to the workflow with `select` rather than caching the
  // narrowed value under a key that promises a Result.
  const targetQuery = useQuery({
    queryKey: queryKeys.resource.get({
      orgId,
      type: ResourceType.Workflow,
      id: targetWorkflowId ?? '',
    }),
    enabled: targetWorkflowId !== undefined && targetWorkflowId !== '',
    queryFn: () =>
      client.resource.get({
        type: ResourceType.Workflow,
        id: targetWorkflowId ?? '',
      }),
    select: (result) =>
      result.isOk() && result.value.type === 'workflow'
        ? result.value.workflow
        : null,
  });

  const entrySpec: WorkflowEntryRunInputSpec | null = useMemo(() => {
    const graph = targetQuery.data?.graph as WorkflowGraphApiData | undefined;
    if (graph === undefined) {
      return null;
    }
    const resolved = resolveWorkflowEntryRunInputSpec(graph);
    if (!resolved.ok) {
      return { kind: 'empty' };
    }
    return resolved.spec;
  }, [targetQuery.data]);

  const { upstreamSamples } = useToolCallUpstreamSamples(node.id);
  const sources = useMemo(
    () => buildUpstreamSources(upstreamSamples),
    [upstreamSamples]
  );
  const sourceLabels = useMemo(
    () =>
      new Map(
        upstreamSamples.map(
          (sample) => [sample.sourceName, sample.label] as const
        )
      ),
    [upstreamSamples]
  );

  useEffect(() => {
    if (targetWorkflowId === undefined || targetWorkflowId === '') {
      onChange(null);
      return;
    }
    const selected = candidates.find(
      (workflow) => workflow.id === targetWorkflowId
    );
    const name =
      selected?.name ??
      readString(asRecord(node.data), 'name') ??
      targetQuery.data?.name;

    if (entrySpec !== null && entrySpec.kind === 'struct') {
      for (const field of entrySpec.fields) {
        if (!field.required) {
          continue;
        }
        const binding = inputBindings[field.name];
        if (binding === undefined || binding.trim() === '') {
          onChange(null);
          return;
        }
      }
    }
    if (entrySpec !== null && entrySpec.kind === 'scalar') {
      const binding = inputBindings[RUN_WORKFLOW_SCALAR_BINDING_KEY];
      if (binding === undefined || binding.trim() === '') {
        onChange(null);
        return;
      }
    }

    const cleaned: Record<string, string> = {};
    for (const [key, value] of Object.entries(inputBindings)) {
      if (value.trim() !== '') {
        cleaned[key] = value;
      }
    }

    onChange({
      type: 'run-workflow',
      data: {
        targetWorkflowId,
        ...(name !== undefined && name !== '' ? { name } : {}),
        ...(Object.keys(cleaned).length > 0 ? { inputBindings: cleaned } : {}),
      },
    });
  }, [
    candidates,
    entrySpec,
    inputBindings,
    node.data,
    onChange,
    targetQuery.data?.name,
    targetWorkflowId,
  ]);

  const setBinding = (key: string, ref: string): void => {
    setInputBindings((prev) => ({ ...prev, [key]: ref }));
  };

  const clearBinding = (key: string): void => {
    setInputBindings((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
  };

  const openTargetWorkflow = (): void => {
    if (
      artifactPanel === null ||
      targetWorkflowId === undefined ||
      targetWorkflowId === ''
    ) {
      return;
    }
    const name =
      candidates.find((workflow) => workflow.id === targetWorkflowId)?.name ??
      targetQuery.data?.name;
    artifactPanel.pushView(
      {
        id: `workflow-graph-${targetWorkflowId}`,
        title: name ?? 'Workflow',
        payload: {
          type: WORKFLOW_GRAPH_VIEW_TYPE,
          workflowId: model.workflowId,
          targetWorkflowId,
          ...(name !== undefined && name !== ''
            ? { targetWorkflowName: name }
            : {}),
        },
      },
      { replaceTop: true }
    );
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      <div className="flex shrink-0 items-center gap-2">
        <Select
          value={targetWorkflowId ?? ''}
          onValueChange={(id) => {
            setTargetWorkflowId(id);
            setInputBindings({});
          }}
        >
          <SelectTrigger className="h-9 w-[220px]">
            <SelectValue placeholder="Select a workflow" />
          </SelectTrigger>
          <SelectContent>
            {candidates.map((workflow) => (
              <SelectItem key={workflow.id} value={workflow.id}>
                {workflow.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {targetWorkflowId !== undefined && targetWorkflowId !== '' ? (
          <Button
            type="button"
            variant="outline"
            className="h-9 gap-2"
            onClick={openTargetWorkflow}
          >
            <ExternalLink className="h-4 w-4" />
            Open workflow
          </Button>
        ) : null}
      </div>

      <div className="min-h-[360px] flex-1">
        <RunWorkflowGraphPreview
          graph={targetQuery.data?.graph as WorkflowGraphApiData | undefined}
          isLoading={
            targetWorkflowId !== undefined &&
            targetWorkflowId !== '' &&
            targetQuery.isPending
          }
        />
      </div>

      {entrySpec !== null && entrySpec.kind === 'struct' ? (
        <div className="flex shrink-0 flex-col gap-2">
          <Text.Caption className="text-muted-foreground block">
            Inputs
          </Text.Caption>
          <Text.Caption className="text-muted-foreground/80 block">
            Map each required input from a previous step’s output.
          </Text.Caption>
          <div className="flex flex-col gap-3">
            {entrySpec.fields.map((field) => (
              <InputBindingRow
                key={field.name}
                label={field.name}
                typeLabel={field.type}
                required={field.required}
                binding={inputBindings[field.name]}
                sources={sources}
                sourceLabels={sourceLabels}
                onPick={(ref) => setBinding(field.name, ref)}
                onClear={() => clearBinding(field.name)}
              />
            ))}
          </div>
        </div>
      ) : null}

      {entrySpec !== null && entrySpec.kind === 'scalar' ? (
        <div className="flex shrink-0 flex-col gap-2">
          <Text.Caption className="text-muted-foreground block">
            Input
          </Text.Caption>
          <Text.Caption className="text-muted-foreground/80 block">
            This workflow expects a single {entrySpec.type} value. Pick where it
            comes from.
          </Text.Caption>
          <InputBindingRow
            label="Value"
            typeLabel={entrySpec.type}
            required
            binding={inputBindings[RUN_WORKFLOW_SCALAR_BINDING_KEY]}
            sources={sources}
            sourceLabels={sourceLabels}
            onPick={(ref) => setBinding(RUN_WORKFLOW_SCALAR_BINDING_KEY, ref)}
            onClear={() => clearBinding(RUN_WORKFLOW_SCALAR_BINDING_KEY)}
          />
        </div>
      ) : null}
    </div>
  );
};
